import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import TourGuide from './components/TourGuide';
import { TourProvider } from './context/TourContext';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Devices = lazy(() => import('./pages/Devices'));
const DeviceInventory = lazy(() => import('./pages/DeviceInventory'));
const Policies = lazy(() => import('./pages/Policies'));
const Deployments = lazy(() => import('./pages/Deployments'));
const RemoteManagement = lazy(() => import('./pages/RemoteManagement'));
const SecurityCompliance = lazy(() => import('./pages/SecurityCompliance'));
const Health = lazy(() => import('./pages/Health'));
const Reports = lazy(() => import('./pages/Reports'));
const Configurations = lazy(() => import('./pages/Configurations'));
const Updates = lazy(() => import('./pages/Updates'));
const Settings = lazy(() => import('./pages/Settings'));
const Users = lazy(() => import('./pages/Users'));
const Login = lazy(() => import('./pages/Login'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const TwoFactorSetup = lazy(() => import('./pages/TwoFactorSetup'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const NotificationTemplates = lazy(() => import('./pages/NotificationTemplates'));
const Logs = lazy(() => import('./pages/Logs'));
const Backups = lazy(() => import('./pages/Backups'));
const ApiDocumentation = lazy(() => import('./pages/ApiDocumentation'));
const Webhooks = lazy(() => import('./pages/Webhooks'));
const Integrations = lazy(() => import('./pages/Integrations'));
const PlatformManagement = lazy(() => import('./pages/PlatformManagement'));
const EDR = lazy(() => import('./pages/EDR'));
const DLP = lazy(() => import('./pages/DLP'));
const ThreatIntelligence = lazy(() => import('./pages/ThreatIntelligence'));
const Forensics = lazy(() => import('./pages/Forensics'));
const Remediation = lazy(() => import('./pages/Remediation'));
const ApplicationControl = lazy(() => import('./pages/ApplicationControl'));
const Compliance = lazy(() => import('./pages/Compliance'));
const DeviceControl = lazy(() => import('./pages/DeviceControl'));
const NASProtection = lazy(() => import('./pages/NASProtection'));
const TenantManagement = lazy(() => import('./pages/TenantManagement'));
const AdvancedAnalytics = lazy(() => import('./pages/AdvancedAnalytics'));

// Loading component
const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <TourProvider>
          <TourGuide />
          <Suspense fallback={<LoadingFallback />}>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices"
            element={
              <ProtectedRoute>
                <Layout>
                  <Devices />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices/:deviceId/inventory"
            element={
              <ProtectedRoute>
                <Layout>
                  <DeviceInventory />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/devices/:deviceId/platform"
            element={
              <ProtectedRoute>
                <Layout>
                  <PlatformManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/policies"
            element={
              <ProtectedRoute>
                <Layout>
                  <Policies />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/deployments"
            element={
              <ProtectedRoute>
                <Layout>
                  <Deployments />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/remote"
            element={
              <ProtectedRoute>
                <Layout>
                  <RemoteManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/security"
            element={
              <ProtectedRoute>
                <Layout>
                  <SecurityCompliance />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/health"
            element={
              <ProtectedRoute>
                <Layout>
                  <Health />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/configurations"
            element={
              <ProtectedRoute>
                <Layout>
                  <Configurations />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/updates"
            element={
              <ProtectedRoute>
                <Layout>
                  <Updates />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/2fa"
            element={
              <ProtectedRoute>
                <Layout>
                  <TwoFactorSetup />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/api-keys"
            element={
              <ProtectedRoute>
                <Layout>
                  <ApiKeys />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <NotificationSettings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notification-templates"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <NotificationTemplates />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <Logs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/backups"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <Backups />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-docs"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <ApiDocumentation />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/webhooks"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <Webhooks />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute requiredRole="admin">
                <Layout>
                  <Integrations />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/edr"
            element={
              <ProtectedRoute>
                <Layout>
                  <EDR />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dlp"
            element={
              <ProtectedRoute>
                <Layout>
                  <DLP />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/threat-intelligence"
            element={
              <ProtectedRoute>
                <Layout>
                  <ThreatIntelligence />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/forensics"
            element={
              <ProtectedRoute>
                <Layout>
                  <Forensics />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/remediation"
            element={
              <ProtectedRoute>
                <Layout>
                  <Remediation />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/application-control"
            element={
              <ProtectedRoute>
                <Layout>
                  <ApplicationControl />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/compliance"
            element={
              <ProtectedRoute>
                <Layout>
                  <Compliance />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/device-control"
            element={
              <ProtectedRoute>
                <Layout>
                  <DeviceControl />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nas-protection"
            element={
              <ProtectedRoute>
                <Layout>
                  <NASProtection />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tenant-management"
            element={
              <ProtectedRoute>
                <Layout>
                  <TenantManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/advanced-analytics"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdvancedAnalytics />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </TourProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
